import { GitBranch, Edit3, Clock, List, Camera, Grid, FileText } from 'lucide-react'

/**
 * Detect the type of a note based on its data structure
 */
export const detectNoteType = (note) => {
  if (!note) return 'STANDARD'
  
  // Check for explicit noteType field first (for new notes)
  if (note.noteType) {
    return note.noteType
  }
  
  // Detect based on data structure (for backward compatibility)
  if (note.flowchartData) return 'FLOWCHART'
  if (note.drawingData) return 'HANDWRITING'
  if (note.audioData) return 'VOICE'
  if (note.itemListData) return 'ITEM_LIST'
  if (note.photoData) return 'PHOTO_NOTE'
  if (note.spreadsheetData) return 'SPREADSHEET'
  
  // Default to standard note
  return 'STANDARD'
}

export const getNoteTypeIcon = (noteType) => {
  switch (noteType) {
    case 'FLOWCHART': return GitBranch
    case 'HANDWRITING': return Edit3
    case 'VOICE': return Clock
    case 'ITEM_LIST': return List
    case 'PHOTO_NOTE': return Camera
    case 'SPREADSHEET': return Grid
    default: return FileText
  }
}

export const getNoteTypeLabel = (noteType) => {
  switch (noteType) {
    case 'FLOWCHART': return 'Flowchart'
    case 'HANDWRITING': return 'Handwriting Note'
    case 'VOICE': return 'Voice Note'
    case 'ITEM_LIST': return 'Item List'
    case 'PHOTO_NOTE': return 'Photo Note'
    case 'SPREADSHEET': return 'Spreadsheet Note'
    default: return 'Standard Note'
  }
}

export const getNoteTypeColor = (noteType) => {
  switch (noteType) {
    case 'FLOWCHART': return 'text-indigo-500 dark:text-indigo-400'
    case 'HANDWRITING': return 'text-purple-500 dark:text-purple-400'
    case 'VOICE': return 'text-red-500 dark:text-red-400'
    case 'ITEM_LIST': return 'text-blue-500 dark:text-blue-400'
    case 'PHOTO_NOTE': return 'text-green-500 dark:text-green-400'
    case 'SPREADSHEET': return 'text-orange-500 dark:text-orange-400'
    default: return 'text-gray-500 dark:text-gray-400'
  }
}

export const isSinglePurposeNote = (noteType) => {
  return noteType !== 'STANDARD'
}

export const getEditorComponentName = (noteType) => {
  switch (noteType) {
    case 'FLOWCHART': return 'FlowchartNoteEditor'
    case 'HANDWRITING': return 'HandwritingNoteEditor'
    case 'VOICE': return 'VoiceNoteEditor'
    case 'ITEM_LIST': return 'ItemListNoteEditor'
    case 'PHOTO_NOTE': return 'PhotoNoteEditor'
    case 'SPREADSHEET': return 'SpreadsheetNoteEditor'
    default: return 'StandardNoteEditor'
  }
}

export const validateNoteTypeData = (note) => {
  const noteType = detectNoteType(note)
  
  switch (noteType) {
    case 'FLOWCHART':
      return note.flowchartData && (note.flowchartData.imageData || note.flowchartData.jsonData)
    case 'HANDWRITING':
      return note.drawingData && (note.drawingData.imageData || note.drawingData.canvasData)
    case 'VOICE':
      return note.audioData && (note.audioData.audioUrl || note.audioData.audioBlob)
    case 'ITEM_LIST':
      return note.itemListData && Array.isArray(note.itemListData.items)
    case 'PHOTO_NOTE':
      return note.photoData && (note.photoData.imageUrl || note.photoData.imageBlob)
    case 'SPREADSHEET':
      return note.spreadsheetData && (note.spreadsheetData.data || note.spreadsheetData.rows)
    case 'STANDARD':
      return true
    default:
      return false
  }
}

export const getDefaultNoteData = (noteType) => {
  const baseData = {
    title: '',
    content: '',
    noteType,
    createdAt: new Date().toISOString(),
    lastModified: new Date().toISOString()
  }
  
  switch (noteType) {
    case 'FLOWCHART':
      return {
        ...baseData,
        flowchartData: {
          imageData: null,
          jsonData: {
            nodes: [],
            edges: [],
            viewport: { x: 0, y: 0, zoom: 1 }
          },
          lastModified: new Date().toISOString()
        }
      }
    case 'HANDWRITING':
      return {
        ...baseData,
        drawingData: {
          imageData: null,
          canvasData: null,
          lastModified: new Date().toISOString()
        }
      }
    case 'VOICE':
      return {
        ...baseData,
        audioData: {
          audioUrl: null,
          audioBlob: null,
          duration: 0,
          transcript: '',
          lastModified: new Date().toISOString()
        }
      }
    case 'ITEM_LIST':
      return {
        ...baseData,
        itemListData: {
          items: [],
          type: 'bullet', // bullet, numbered, checkbox
          title: '',
          lastModified: new Date().toISOString()
        }
      }
    case 'PHOTO_NOTE':
      return {
        ...baseData,
        photoData: {
          imageUrl: null,
          imageBlob: null,
          annotations: [],
          lastModified: new Date().toISOString()
        }
      }
    case 'SPREADSHEET':
      return {
        ...baseData,
        spreadsheetData: {
          rows: 10,
          cols: 5,
          data: [],
          formulas: {},
          lastModified: new Date().toISOString()
        }
      }
    default:
      return baseData
  }
}
